"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useRef } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import { Container, SectionTitle, SectionSubHeading } from "@/components";
import { Search, MessageSquare, Code, CheckCircle } from "lucide-react";
import { fadeInUp } from "@/lib/animation/variant";
const processData = [{
  id: "01",
  icon: <Search />,
  title: "The Discovery",
  description: "Your journey starts here. We don't just look for resumes; we seek stories, passion, and the 'hunger' to create the next big digital thing."
}, {
  id: "02",
  icon: <MessageSquare />,
  title: "Vibe Check",
  description: "Skills can be taught; culture cannot. This is a 1:1 relaxed conversation to see if our frequencies align perfectly."
}, {
  id: "03",
  icon: <Code />,
  title: "Craft Review",
  description: "No 'Leetcoding' here. Show us how you solve real-world problems. Whether design or code, show us your process and logic."
}, {
  id: "04",
  icon: <CheckCircle />,
  title: "Joining Octarnal",
  description: "The final leap. Welcome to a high-speed agency environment where your every decision shapes our global projects."
}];
const CareerProcess = () => {
  const $ = _c(12);
  const containerRef = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      target: containerRef,
      offset: ["start end", "end end"]
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    scrollYProgress
  } = useScroll(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      stiffness: 100,
      damping: 30
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const scaleY = useSpring(scrollYProgress, t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="hidden lg:block absolute top-0 left-0 text-[21vw] font-black text-white/2 uppercase leading-none pointer-events-none select-none">Process</div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      once: true
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <motion.div custom={0} initial="hidden" whileInView="visible" viewport={t3} variants={fadeInUp}><SectionTitle title="Recruitment Path" className="text-white" /></motion.div>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      once: true
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="mb-20">{t4}<motion.div custom={1} initial="hidden" whileInView="visible" viewport={t5} variants={fadeInUp}><SectionSubHeading subtitle="Understand our transparent and efficient journey." className="mt-1 text-primary border-primary/20 text-xl font-bold uppercase tracking-widest" /></motion.div></div>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== scaleY) {
    t7 = <motion.div style={{
      scaleY
    }} className="absolute left-1/2 -translate-x-1/2 w-0.5 h-full bg-linear-to-b from-primary to-transparent origin-top hidden lg:block opacity-40 shadow-[0_0_15px_rgb(178, 209, 39)]" />;
    $[7] = scaleY;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = processData.map(_temp);
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== t7) {
    t9 = <section ref={containerRef} className="relative md:py-16 py-10 bg-accent-foreground overflow-hidden">{t2}<Container className="px-4 lg:px-10 relative z-10">{t6}<div className="relative max-w-6xl mx-auto">{t7}{t8}</div></Container></section>;
    $[10] = t7;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  return t9;
};
export default CareerProcess;
function _temp(item, index) {
  return <div key={item.id} className={`flex flex-col lg:flex-row items-center gap-8 lg:gap-20 mb-10 md:mb-20 lg:mb-30 last:mb-0 ${index % 2 !== 0 ? "lg:flex-row-reverse" : ""}`}>{" "}<motion.div initial={{
      scale: 0,
      rotate: -45
    }} whileInView={{
      scale: 1,
      rotate: 0
    }} viewport={{
      once: true
    }} className="w-32 h-32 rounded-full border border-white/10 bg-accent-foreground flex items-center justify-center group hover:border-primary transition-all duration-700 shadow-2xl overflow-hidden lg:hidden group-hover:bg-primary/5 "><span className="text-white text-3xl lg:text-5xl font-black group-hover:text-primary transition-colors duration-500">{item.id}</span></motion.div><div className="flex-1 text-center lg:text-left"><motion.div initial={{
        opacity: 0,
        x: index % 2 !== 0 ? 50 : -50
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true,
        margin: "-100px"
      }} transition={{
        duration: 1,
        ease: [0.76, 0, 0.24, 1]
      }}><span className="text-primary font-black text-lg lg:text-xl tracking-[0.5em] mb-4 uppercase italic hidden lg:block">Step {item.id}</span><SectionTitle title={item.title} className="text-white mb-4 lg:mb-8 leading-tight tracking-tighter text-2xl md:text-4xl xl:text-5xl" /><p className="text-white/40 text-lg lg:text-xl leading-relaxed font-medium max-w-md mx-auto lg:mx-0">{item.description}</p></motion.div></div><div className="relative shrink-0 z-20 hidden lg:block"><motion.div initial={{
        scale: 0,
        rotate: -45
      }} whileInView={{
        scale: 1,
        rotate: 0
      }} viewport={{
        once: true
      }} className="lg:w-40 lg:h-40 rounded-full border border-white/10 bg-accent-foreground flex items-center justify-center group hover:border-primary transition-all duration-700 shadow-2xl overflow-hidden"><span className="text-white text-3xl lg:text-5xl font-black group-hover:text-primary transition-colors duration-500">{item.id}</span><div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/5 transition-all duration-500 rounded-full" /></motion.div></div><div className="flex-1 hidden lg:block" /></div>;
}